import React from 'react';
import { LOGICAL_OPERATORS, COMPARISON_OPERATORS } from '../../../config/api';

/**
 * LogicalOperatorSelector - Toggle between AND/OR operators
 */
export const LogicalOperatorSelector = ({
  value,
  onChange,
  disabled = false,
  rulesSchema = null,
}) => {
  // Use schema if available, otherwise fallback to constants
  const operators = rulesSchema?.logicalOperators
    ? rulesSchema.logicalOperators.map(op => ({
      value: op.name,
      label: op.name, // Usually short (AND, OR)
      description: op.description,
    }))
    : Object.values(LOGICAL_OPERATORS);

  return (
    <div className="fieldset">
      <label className="label">
        <span className="text-sm text-base-content/70">Combine conditions with:</span>
      </label>
      <div className="join w-full">
        {operators.map((op) => (
          <button
            key={op.value}
            type="button"
            className={`join-item btn flex-1 flex-col h-auto py-2 ${value === op.value ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => onChange(op.value)}
            disabled={disabled}
          >
            <span className="font-bold">{op.label || op.value}</span>
            {op.description && <span className="text-xs opacity-70">{op.description}</span>}
          </button>
        ))}
      </div>
    </div>
  );
};

/**
 * ComparisonOperatorSelector - Dropdown for comparison operators
 */
export const ComparisonOperatorSelector = ({
  value,
  onChange,
  error,
  disabled = false,
  required = false,
  label = 'Comparison',
  allowedOperators = null,
  rulesSchema = null,
  operators: passedOperators = null,
}) => {
  const handleChange = (e) => {
    onChange(e.target.value);
  };

  // Build operators list
  let operators;
  if (passedOperators) {
    // If operators are passed directly
    let entries;
    if (Array.isArray(passedOperators)) {
      entries = passedOperators.map(op => [op.name, op]);
    } else {
      entries = Object.entries(passedOperators);
    }

    if (allowedOperators) {
      entries = entries.filter(([key]) => allowedOperators.includes(key));
    }

    operators = entries.map(([key, op]) => ({
      key,
      label: op.label || op.description || key,
    }));
  } else if (rulesSchema?.comparisonOperators) {
    // Legacy/Fallback for global operators
    operators = rulesSchema.comparisonOperators.map(op => ({
      key: op.name,
      label: `${op.description} (${op.symbol})`,
    }));
    if (allowedOperators) {
      operators = operators.filter(op => allowedOperators.includes(op.key));
    }
  } else {
    // Fallback to constants
    const entries = allowedOperators
      ? Object.entries(COMPARISON_OPERATORS).filter(([key]) => allowedOperators.includes(key))
      : Object.entries(COMPARISON_OPERATORS);
    operators = entries.map(([key, { label: opLabel }]) => ({
      key,
      label: opLabel,
    }));
  }

  return (
    <div className="fieldset w-full">
      <label className="label">
        <span className="text-sm">{label}{required && <span className="text-error">*</span>}</span>
      </label>
      <select
        className={`select select w-full ${error ? 'select-error' : ''}`}
        value={value || ''}
        onChange={handleChange}
        disabled={disabled}
        required={required}
      >
        <option value="" disabled>Select comparison</option>
        {operators.map(({ key, label: opLabel }) => (
          <option key={key} value={key}>{opLabel}</option>
        ))}
      </select>
      {error && <label className="label"><span className="text-xs text-base-content/60 text-error">{error}</span></label>}
    </div>
  );
};

export default { LogicalOperatorSelector, ComparisonOperatorSelector };
